import { evaluateCondition } from "./definition/condition";
import type {
  CarouselColumnsSubstepDefinition,
  ConfigurationAttachmentSubstepDefinition,
  ConfiguratorDefinition,
  ControlDefinition,
  GenericSubstepDefinition,
  StepDefinition,
  SubstepDefinition,
} from "../contract/definition";
import type { ConfiguratorConfiguration, ConfiguratorInstanceContext } from "../contract/configuration";
import { resolveInstanceContexts } from "./instanceContext";
import type { StepStatus } from "../contract/step";

export type ControlError = "required" | "conflict" | null;

export interface ConfiguratorDerivationOptions {
  requiredValidationStepIndexes?: ReadonlySet<number>;
}

export interface ResolvedControlModel {
  definition: ControlDefinition;
  controlIndex: number;
  context: ConfiguratorInstanceContext;
  visible: boolean;
  disabled: boolean;
  error: ControlError;
  complete: boolean;
}

interface ResolvedSubstepModelBase {
  substepIndex: number;
  context: ConfiguratorInstanceContext;
  visible: boolean;
  controls: readonly ResolvedControlModel[];
}

export interface ResolvedGenericSubstepModel extends ResolvedSubstepModelBase {
  renderer: "generic";
  definition: GenericSubstepDefinition;
}

export interface ResolvedCarouselColumnModel {
  carousel: ResolvedControlModel;
  details: readonly ResolvedControlModel[];
}

export interface ResolvedCarouselColumnsSubstepModel extends ResolvedSubstepModelBase {
  renderer: "carouselColumns";
  definition: CarouselColumnsSubstepDefinition;
  columns: readonly ResolvedCarouselColumnModel[];
}

export interface ResolvedConfigurationAttachmentSubstepModel extends ResolvedSubstepModelBase {
  renderer: "configurationAttachment";
  definition: ConfigurationAttachmentSubstepDefinition;
  attachment: ConfiguratorConfiguration["attachment"];
}

export type ResolvedSubstepModel =
  | ResolvedGenericSubstepModel
  | ResolvedCarouselColumnsSubstepModel
  | ResolvedConfigurationAttachmentSubstepModel;

const isSubstepComplete = (substep: ResolvedSubstepModel) => {
  if (!substep.visible) {
    return true;
  }

  if (substep.renderer === "configurationAttachment") {
    return substep.definition.required !== true || substep.attachment !== undefined;
  }

  return substep.controls.every((control) => control.complete);
};

export interface ResolvedStepModel {
  definition: StepDefinition;
  stepIndex: number;
  visible: boolean;
  status: StepStatus;
  complete: boolean;
  conflicted: boolean;
  dependencyUnlocked: boolean;
  reached: boolean;
  conflictRecovery: boolean;
  editable: boolean;
  satisfied: boolean;
  error: boolean;
  substeps: readonly ResolvedSubstepModel[];
}

const hasValue = (value: string | null | undefined) => value !== null && value !== undefined && value !== "";

const hasPresentedSelection = (control: ControlDefinition, context: ConfiguratorInstanceContext) => {
  if (!("required" in control) || control.required !== true) {
    return true;
  }

  const cstic = context.instance.cstics[control.cstic];
  return cstic?.values.some(
    (value) => value.value === cstic.value && (control.hideDisabled !== true || value.selectable),
  );
};

const hasControlConflict = (
  configuration: ConfiguratorConfiguration,
  context: ConfiguratorInstanceContext,
  control: ControlDefinition,
) => {
  const representedCsticKeys = new Set(
    [control.cstic, ...(control.conflictCstics ?? [])]
      .map((name) => context.instance.cstics[name]?.key)
      .filter((key): key is string => Boolean(key)),
  );

  return configuration.conflicts.some((conflict) =>
    conflict.cstics.some((cstic) => representedCsticKeys.has(cstic.key)),
  );
};

const resolveControl = (
  configuration: ConfiguratorConfiguration,
  context: ConfiguratorInstanceContext,
  control: ControlDefinition,
  controlIndex: number,
  showRequiredError: boolean,
): ResolvedControlModel => {
  const cstic = context.instance.cstics[control.cstic];
  const visible = cstic?.visible !== false && evaluateCondition(control.visibleWhen, context);
  const disabled = control.disabledWhen ? evaluateCondition(control.disabledWhen, context) : false;
  const hasConflict = visible && hasControlConflict(configuration, context, control);
  const missingRequiredValue =
    control.type !== "Hero" &&
    visible &&
    !disabled &&
    (!hasValue(cstic?.value) || !hasPresentedSelection(control, context));
  const error: ControlError = hasConflict ? "conflict" : showRequiredError && missingRequiredValue ? "required" : null;

  return {
    definition: control,
    controlIndex,
    context,
    visible,
    disabled,
    error,
    complete: !hasConflict && !missingRequiredValue,
  };
};

const resolveSubstep = (
  definition: SubstepDefinition,
  substepIndex: number,
  context: ConfiguratorInstanceContext,
  configuration: ConfiguratorConfiguration,
  showRequiredErrors: boolean,
): ResolvedSubstepModel => {
  const visible = evaluateCondition(definition.visibleWhen, context);

  if (definition.renderer === "carouselColumns") {
    let controlIndex = 0;
    const columns = definition.columns.map((column) => {
      const carousel = resolveControl(configuration, context, column.carousel, controlIndex, showRequiredErrors);
      controlIndex += 1;
      const details = (column.details ?? []).map((detail) => {
        const resolvedDetail = resolveControl(configuration, context, detail, controlIndex, showRequiredErrors);
        controlIndex += 1;
        return resolvedDetail;
      });

      return { carousel, details };
    });

    return {
      renderer: "carouselColumns",
      definition,
      substepIndex,
      context,
      visible,
      controls: columns.flatMap((column) => [column.carousel, ...column.details]),
      columns,
    };
  }

  if (definition.renderer === "configurationAttachment") {
    return {
      renderer: "configurationAttachment",
      definition,
      substepIndex,
      context,
      visible,
      controls: [],
      attachment: configuration.attachment,
    };
  }

  return {
    renderer: "generic",
    definition,
    substepIndex,
    context,
    visible,
    controls: definition.controls.map((control, controlIndex) =>
      resolveControl(configuration, context, control, controlIndex, showRequiredErrors),
    ),
  };
};

const resolveStepSubsteps = (
  step: StepDefinition,
  stepIndex: number,
  configuration: ConfiguratorConfiguration,
  options: ConfiguratorDerivationOptions,
): ResolvedSubstepModel[] =>
  step.substeps.flatMap((substep, substepIndex) =>
    resolveInstanceContexts(configuration, substep.instanceTarget).map((context) =>
      resolveSubstep(
        substep,
        substepIndex,
        context,
        configuration,
        options.requiredValidationStepIndexes?.has(stepIndex) ?? false,
      ),
    ),
  );

export const deriveConfiguratorSteps = (
  definition: ConfiguratorDefinition,
  configuration: ConfiguratorConfiguration,
  options: ConfiguratorDerivationOptions = {},
): ResolvedStepModel[] => {
  const mainContext = resolveInstanceContexts(configuration)[0];
  const resolvedById = new Map<string, ResolvedStepModel>();

  return definition.steps.map((step, stepIndex): ResolvedStepModel => {
    const substeps = resolveStepSubsteps(step, stepIndex, configuration, options);
    const visibleControls = substeps.filter((substep) => substep.visible).flatMap((substep) => substep.controls);
    const visible = evaluateCondition(step.visibleWhen, mainContext);
    const disabled = step.disabledWhen ? evaluateCondition(step.disabledWhen, mainContext) : false;
    const complete = !visible || (!disabled && substeps.filter((substep) => substep.visible).every(isSubstepComplete));
    const conflicted = visible && visibleControls.some((control) => control.error === "conflict");
    const dependencyUnlocked =
      !step.unlockedWhen ||
      step.unlockedWhen.allStepsSatisfied.every((stepId) => resolvedById.get(stepId)?.satisfied === true);
    const reached = stepIndex + 1 <= configuration.highestVisitedStepNumber;
    const conflictRecovery = !dependencyUnlocked && reached && conflicted;
    const editable = visible && !disabled && reached && (dependencyUnlocked || conflictRecovery);
    const satisfied = !visible || (dependencyUnlocked && (complete || disabled));
    const status: StepStatus =
      !visible || disabled
        ? "disabled"
        : !dependencyUnlocked && !conflictRecovery
        ? "locked"
        : complete
        ? "completed"
        : "available";

    const resolved = {
      definition: step,
      stepIndex,
      visible,
      status,
      complete,
      conflicted,
      dependencyUnlocked,
      reached,
      conflictRecovery,
      editable,
      satisfied,
      error: conflicted,
      substeps,
    };
    resolvedById.set(step.id, resolved);
    return resolved;
  });
};

export const isConfigurationComplete = (
  steps: readonly ResolvedStepModel[],
  configuration: Pick<ConfiguratorConfiguration, "conflicts">,
) => configuration.conflicts.length === 0 && steps.every((step) => step.satisfied);
